/**
 * Moving or renaming your own folders when a shared folder is involved (E10, P1-A14): a shared
 * folder, or a folder above one, in My Files. The share follows to the new path, so its members
 * keep access (`JobService/lambda/code/python/shares/moves.py` in omicscloud-cdk).
 *
 * The storage API still refuses these (409 `shared_folder_locked`); they go to
 * `POST /v1/shares/moves` instead, which copies everything server side in the background, switches
 * the shares to the new path, then deletes the originals. The client polls
 * `GET /v1/shares/moves/{operationId}` until it ends. While it runs, the shares it carries are
 * read-only for their members (`moving` on the share, writes answer 409 `share_moving`).
 *
 * Paths are relative to My Files: `a/b.txt` for a file, `a/` for a folder.
 */
import {API} from 'aws-amplify'
import {apiCallWithAuthCheck} from '../auth/core/AuthErrorInterceptor'
import {
  isKnownShareErrorCode,
  safeServerMessage,
  SHARE_ERROR_MESSAGES,
  ShareErrorCode,
  shareErrorMessage,
} from './errors'
import {pollOperation, PollOperationOptions, ShareOperationTimeoutError} from './shareOperations'

const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'

/** A shared folder a move carries along. `from` and `to` are its folder paths, ending in `/`. */
export interface ShareMove {
  shareId: string
  name: string
  from: string
  to: string
  /** The share's new name, when it was named after its folder and the folder is renamed. */
  newName?: string
}

/**
 * What to move: items into a folder (`destination` ends in `/`, or is `''` for the top of My
 * Files; each source lands at `destination + its name`), or one item renamed in place.
 */
export type OwnerMoveBody =
  | {sources: string[]; destination: string}
  | {path: string; newName: string}

/** A dry run's answer: the shared folders the move would carry, and the targets already taken. */
export interface OwnerMovePlan {
  shares: ShareMove[]
  /** Paths that already exist where the items would land. */
  conflicts: string[]
}

export type OwnerMoveStatus = 'queued' | 'running' | 'succeeded' | 'failed'
export type OwnerMovePhase = 'count' | 'copy' | 'switch' | 'delete' | 'sweep' | 'done'

export interface OwnerMoveStarted {
  operationId: string
  status: OwnerMoveStatus
  shares: ShareMove[]
}

/** `GET /v1/shares/moves/{operationId}`. */
export interface OwnerMoveOperation {
  operationId: string
  status: OwnerMoveStatus
  phase?: OwnerMovePhase
  /** Objects to copy (known after the `count` phase), and bytes. */
  objects?: number
  bytes?: number
  /** Objects and bytes copied so far. */
  done?: number
  doneBytes?: number
  /** Originals that could not be deleted from the old location. */
  leftoverCount?: number
  /**
   * Why it ended badly: on `failed` (`interrupted`, `internal_error`, …) nothing moved; on
   * `succeeded`, `cleanup_incomplete` means it moved but some originals were left behind.
   */
  error?: string
  /** The server's words for {@link error}. Show only through `safeServerMessage`. */
  message?: string
  shares?: ShareMove[]
  mappings?: {from: string; to: string}[]
  createdAt?: string
  updatedAt?: string
  finishedAt?: string
}

const MOVES_ROUTE = 'v1/shares/moves'

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

/** `POST /v1/shares/moves` with `dryRun`: nothing changes. */
export async function dryRunOwnerMove(body: OwnerMoveBody): Promise<OwnerMovePlan> {
  const response = await apiCallWithAuthCheck(() =>
    API.post(apiName, MOVES_ROUTE, {body: {...body, dryRun: true}})
  )
  return {shares: asArray(response?.shares), conflicts: asArray(response?.conflicts)}
}

/** `POST /v1/shares/moves`: starts the move (202) and returns its operation. */
export async function startOwnerMove(body: OwnerMoveBody): Promise<OwnerMoveStarted> {
  const response = await apiCallWithAuthCheck(() => API.post(apiName, MOVES_ROUTE, {body}))
  return {
    operationId: String(response?.operationId ?? ''),
    status: response?.status ?? 'queued',
    shares: asArray(response?.shares),
  }
}

export const getOwnerMove = (operationId: string): Promise<OwnerMoveOperation> =>
  apiCallWithAuthCheck(() =>
    API.get(apiName, `${MOVES_ROUTE}/${encodeURIComponent(operationId)}`, {})
  )

/**
 * Every 2 seconds. There is no size cap (the worker hands over to a fresh invocation of itself),
 * and the server reports a move that stopped making progress as ended, so the timeout is only a
 * backstop. Tests shorten the intervals.
 */
export const ownerMovePolling = {
  intervalMs: 2000,
  maxIntervalMs: 2000,
  timeoutMs: 6 * 60 * 60 * 1000,
}

/** The move ended as `failed`: nothing moved. */
export class OwnerMoveError extends Error {
  readonly operation: OwnerMoveOperation
  constructor(operation: OwnerMoveOperation) {
    super(`The move failed${operation.error ? ` (${operation.error})` : ''}.`)
    this.name = 'OwnerMoveError'
    this.operation = operation
  }
}

/**
 * Polls a move until it ends. Resolves with the operation when it `succeeded` (check
 * {@link ownerMoveWarning}); rejects with {@link OwnerMoveError} when it `failed`, with
 * `ShareOperationTimeoutError` when polling gives up, and with the API's error otherwise.
 */
export async function waitForOwnerMove(
  operationId: string,
  options: PollOperationOptions<OwnerMoveOperation> = {}
): Promise<OwnerMoveOperation> {
  const last = await pollOperation(() => getOwnerMove(operationId), {
    ...ownerMovePolling,
    ...options,
  })
  if (last.status === 'failed') {
    throw new OwnerMoveError(last)
  }
  return last
}

const CLEANUP_INCOMPLETE = 'Moved. Some original files may be left at the old location.'
const NOTHING_MOVED = "The move didn't finish, and nothing was moved. Please try again."

/** For a move that `succeeded` but left some originals behind: what to say. Otherwise undefined. */
export function ownerMoveWarning(operation: OwnerMoveOperation): string | undefined {
  if (operation.error !== 'cleanup_incomplete') {
    return undefined
  }
  return safeServerMessage(operation.message) ?? CLEANUP_INCOMPLETE
}

/** How the general texts read in the context of moving your own folders. */
const MOVE_OVERRIDES: Partial<Record<ShareErrorCode, string>> = {
  invalid_name: "That name isn't valid. It can't contain / or control characters.",
  invalid_path: "That path isn't valid, or it's too long.",
  invalid_folder_path: "A shared folder can't have that path. Choose another name or place.",
  unsupported_character:
    "A shared folder's path can't contain * ? $ quotes, backslashes or control characters. Choose another name or place.",
}

/** Why a move or rename of your own folders failed, for the dialog or a toast. */
export function ownerMoveErrorMessage(error: unknown): string {
  if (error instanceof OwnerMoveError) {
    const {error: code, message} = error.operation
    const known = isKnownShareErrorCode(code) ? SHARE_ERROR_MESSAGES[code].message : undefined
    return safeServerMessage(message) ?? known ?? NOTHING_MOVED
  }
  if (error instanceof ShareOperationTimeoutError) {
    return "It's taking longer than expected. Refresh My Files later to see the result."
  }
  return shareErrorMessage(error, {overrides: MOVE_OVERRIDES})
}
